import {CLOUD_SCHEME, tryParseCloudPath} from '@/shared/utils/cloudPath'
import {plainFileFormat} from './plain-file-formats'

/**
 * How an image a document references by storage path (`![](plot.png)`, `![](cloud:OmicsAI/plot.png)`)
 * becomes a key in the same space as the document. Pure, so the rules can be pinned by tests; the
 * signing itself lives in `document-resources.ts`.
 *
 * Relative paths resolve against the document's own folder, the way GitHub, VS Code and a browser
 * resolve them, so a report keeps rendering after it is downloaded together with its images. A
 * `cloud:` path and a `/`-rooted path start at the root of the space the document is in.
 */

/** The image formats a document may reference: the ones the browser renders in an `<img>`. */
const IMAGE_FORMATS: ReadonlySet<string> = new Set(['png', 'jpeg', 'gif', 'webp', 'bmp', 'svg'])

/** A leading `scheme:`, as in `https:` or `data:`. */
const SCHEME = /^[a-z][a-z0-9+.-]*:/i

/** `%20` and friends: `marked` percent-encodes the paths it renders. Kept as-is when malformed. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Walk `segments` from `base`, resolving `.` and `..`. Undefined when a `..` climbs above the root of
 * the space: the reference then points nowhere this document may reach.
 */
function joinSegments(base: readonly string[], segments: readonly string[]): string[] | undefined {
  const resolved = [...base]
  for (const segment of segments) {
    if (segment === '' || segment === '.') continue
    if (segment === '..') {
      if (resolved.length === 0) return undefined
      resolved.pop()
      continue
    }
    resolved.push(segment)
  }
  return resolved
}

/** The path below the root of the space, or undefined when `ref` does not name a storage file. */
function referencedKey(ref: string, documentKey: string): string | undefined {
  // A query or fragment is meaningless for a storage file (and `#` is how a name containing one is
  // written in a URL anyway: `%23`).
  const path = ref.trim().replace(/[?#].*$/, '')
  if (path === '' || path.includes('\0')) return undefined

  if (path.startsWith(CLOUD_SCHEME)) {
    // The spec forbids `..` in a cloud path; tryParseCloudPath enforces it and the other rules.
    const parsed = tryParseCloudPath(path.split('/').map(decodeSegment).join('/'))
    return parsed && !parsed.isFolder ? parsed.rel : undefined
  }
  if (SCHEME.test(path) || path.startsWith('//')) return undefined

  const rooted = path.startsWith('/')
  const base = rooted ? [] : documentKey.split('/').filter(Boolean).slice(0, -1)
  const segments = joinSegments(base, path.split('/').map(decodeSegment))
  if (!segments || segments.length === 0 || path.endsWith('/')) return undefined
  return segments.join('/')
}

/**
 * The key, in the document's space, of an image `ref` that the document at `documentKey` names, or
 * undefined when `ref` is not an image in that space.
 *
 * @param ref         an image `src` as the viewer found it in the rendered document
 * @param documentKey the document's own key below the root of its space, e.g. `OmicsAI/run/report.md`
 */
export function resolveDocumentImage(ref: string, documentKey: string): string | undefined {
  const key = referencedKey(ref, documentKey)
  if (!key) return undefined
  const format = plainFileFormat(key)
  return format && IMAGE_FORMATS.has(format) ? key : undefined
}
